import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNegocio } from '../contexts/NegocioContext';
import { Eye, EyeOff } from 'lucide-react';

function formatApiError(detail) {
  if (detail == null) return 'Error de conexión. Intenta de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function Login() {
  const { login } = useAuth();
  const { nombreNegocio, iconoNegocio } = useNegocio();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Ingresa tu usuario y contraseña');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center px-8" data-testid="login-page">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="text-7xl mb-4 leading-none" role="img" aria-label={nombreNegocio}>
          {iconoNegocio}
        </div>
        <h1 className="text-4xl font-black text-zinc-900 leading-tight">{nombreNegocio}</h1>
        <p className="text-zinc-500 text-base mt-2">Sistema de ventas</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center" data-testid="login-error">
          {error}
        </div>
      )}

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-zinc-600 text-lg mb-2">Usuario</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-14 px-4 text-lg border border-zinc-300 rounded-xl focus:border-zinc-500 focus:ring-0 outline-none"
            placeholder="tu@email.com"
            data-testid="login-email"
            autoComplete="email"
          />
        </div>

        <div>
          <label className="block text-zinc-600 text-lg mb-2">Contraseña</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-14 px-4 pr-14 text-lg border border-zinc-300 rounded-xl focus:border-zinc-500 focus:ring-0 outline-none"
              placeholder="••••••••"
              data-testid="login-password"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400"
              data-testid="login-toggle-password"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="pt-8">
          <button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-white text-zinc-900 border border-zinc-300 rounded-xl text-lg font-medium hover:bg-zinc-50 active:bg-zinc-100 transition-colors disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? (
              <span className="inline-block w-5 h-5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
            ) : (
              'Ingresar'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
